import {
  XOP_QUOTE_STATUS_LABELS,
  type XopQuote,
  type XopQuoteEffectiveStatus,
} from "@xop-any-time/shared";
import { Chip } from "@mui/material";
import type { JSX } from "react";

import { date, dateOnly } from "../../../lib/format.js";

/**
 * The three pieces of quote furniture the list and the document page share,
 * so a quote cannot be described one way in the table and another on its own
 * page. Modelled on `licences/licence-ui.tsx`.
 */

/**
 * Colour carries MEANING here, not decoration.
 *
 * `open` is the only state with something to do, so it is the only one that
 * uses the accenting `info`; `expired` is a warning because it is an
 * opportunity that lapsed; `declined` is deliberately neutral rather than red
 * — the reseller declined it on purpose, and colouring its own decision as an
 * error is the interface arguing with the user.
 */
const CHIP_COLOR: Record<XopQuoteEffectiveStatus, "info" | "success" | "warning" | "default"> = {
  open: "info",
  accepted: "success",
  expired: "warning",
  declined: "default",
};

export function XopQuoteStatusChip({ status }: { status: XopQuoteEffectiveStatus }): JSX.Element {
  return (
    <Chip
      size="small"
      variant={status === "declined" ? "outlined" : "filled"}
      color={CHIP_COLOR[status]}
      label={XOP_QUOTE_STATUS_LABELS[status]}
      sx={{ height: 20, fontSize: 11.5, fontWeight: 600 }}
    />
  );
}

/**
 * "Valid until 9 Oct 2026" / "Expired on 1 Aug 2026".
 *
 * The tense is the whole point: a lapsed quote that still says "valid until"
 * reads as an offer that is still open, which is the one thing this section
 * must never imply.
 */
export function validityPhrase(quote: Pick<XopQuote, "effectiveStatus" | "validUntil">): string {
  if (quote.validUntil === null) return "No expiry date";
  // `validUntil` is a date-only column (`YYYY-MM-DD`), so `dateOnly` — not
  // `date` — parses it: `date`'s UTC-midnight round-trip renders it a day
  // early for anyone west of UTC, which for a lapse deadline is the one
  // direction this must never be wrong in.
  return quote.effectiveStatus === "expired"
    ? `Expired on ${dateOnly(quote.validUntil)}`
    : `Valid until ${dateOnly(quote.validUntil)}`;
}

/** "Accepted on 9 Sep 2026 by dana@…" — null while the quote is undecided. */
export function decisionPhrase(
  quote: Pick<XopQuote, "effectiveStatus" | "decidedAt" | "decidedByEmail">,
): string | null {
  if (quote.effectiveStatus !== "accepted" && quote.effectiveStatus !== "declined") return null;
  const verb = quote.effectiveStatus === "accepted" ? "Accepted" : "Declined";
  const when = quote.decidedAt ? ` on ${date(quote.decidedAt)}` : "";
  const who = quote.decidedByEmail ? ` by ${quote.decidedByEmail}` : "";
  return `${verb}${when}${who}`;
}
